/* eslint-disable consistent-return */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ProgressBar, Spinner } from 'react-bootstrap';
import { DateTime } from 'luxon';
import {
  UserPlus,
  Send,
  Download,
  FileX,
  Trash,
  Cross,
  Upload,
  UserCheck,
} from '@saleshandy/icons';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@saleshandy/design-system';
import {
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';
import { RequestStatus } from '../../../../shared/enums/request-status';
import { SortOrder } from '../../../../shared/enums/order';
import TanStackTable from '../../../../shared/design-system/ui/tanstack-table';
import type { SortConfig } from '../../../../shared/design-system/ui/tanstack-table/types';
import {
  EnrichmentStatus,
  EnrichmentType,
} from '../../enums/csv-enrichment.enums';
import { EnrichmentJob } from '../../types/csv-enrichment.types';
import {
  getComponentDisplayLabel,
  getStatusDisplayLabel,
  getStatusColorClass,
  API_TO_COLUMN_SORT_KEY,
  COLUMN_TO_API_SORT_KEY,
} from '../../helpers/csv-enrichment.helpers';
import EmptyList from '../../../../shared/design-system/components/molecules/empty-list/empty-list';
import { Images } from '../../../../shared/app-constants';
import TruncatedTextWithAutoTooltip from '../../../../shared/components/truncated-text-with-auto-tooltip';
import { useCanShowDataExport } from '../../../../shared/utils/data-export-permission';

type EnrichmentTableProps = {
  jobs: EnrichmentJob[];
  requestStatus: RequestStatus;
  hasActiveFilters: boolean;
  page: number;
  pageSize: number;
  totalJobs: number;
  sort: SortConfig | null;
  onSort: (sortBy: string, order: SortOrder) => void;
  onPaginationChange: (payload: { page?: number; limit?: number }) => void;
  onAddToProspects: (id: string) => void;
  onAddToSequence: (id: string) => void;
  onExport: (id: string) => void;
  onFailedReport: (id: string) => void;
  onDelete: (id: string) => void;
  onCancel: (id: string) => void;
  onNewEnrichment: () => void;
  onRetry: () => void;
  onViewJob?: (id: string) => void;
};

const columnHelper = createColumnHelper<EnrichmentJob>();

const SKELETON_ROWS = 5;

type DownloadActionProps = {
  job: EnrichmentJob;
  onExport: (id: string) => void | Promise<void>;
  onFailedReport: (id: string) => void | Promise<void>;
};

const DownloadAction: React.FC<DownloadActionProps> = ({
  job,
  onExport,
  onFailedReport,
}) => {
  const canDisplayDataExport = useCanShowDataExport();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const enrichedCount = job.enrichedRecords ?? 0;
  const failedCount = job.failedRecords ?? 0;
  const hasBoth = enrichedCount > 0 && failedCount > 0;
  const hasFailedOnly = enrichedCount === 0 && failedCount > 0;
  const hasNone = enrichedCount === 0 && failedCount === 0;

  const isDisabled =
    job.status === EnrichmentStatus.PROCESSING ||
    job.status === EnrichmentStatus.QUEUED ||
    hasNone;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        !buttonRef.current?.contains(e.target as Node) &&
        !popoverRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => setIsOpen(false);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  if (!canDisplayDataExport) {
    return null;
  }

  const handleClick = async () => {
    if (isDisabled || isLoading) return;
    if (hasBoth) {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) {
        setPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
        });
      }
      setIsOpen((prev) => !prev);
      return;
    }
    setIsLoading(true);
    try {
      if (hasFailedOnly) {
        await onFailedReport(job.id);
      } else {
        await onExport(job.id);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line no-nested-ternary
  const tooltipText = hasFailedOnly
    ? 'Export Failed Report'
    : hasBoth
    ? 'Export'
    : 'Export Enriched Report';

  return (
    <div className="bs-csv-enrichment__download-action">
      <OverlayTooltip text={tooltipText} placement={Placement.Bottom}>
        <button
          ref={buttonRef}
          type="button"
          className="bs-csv-enrichment__action-btn"
          disabled={isDisabled || isLoading}
          onClick={handleClick}
        >
          {isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <Download width={16} height={16} />
          )}
        </button>
      </OverlayTooltip>

      {hasBoth &&
        isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            className="bs-csv-enrichment__download-popover"
            style={{ top: position.top, right: position.right }}
          >
            <button
              type="button"
              className="bs-csv-enrichment__download-popover-item"
              onClick={() => {
                onExport(job.id);
                setIsOpen(false);
              }}
            >
              <Download width={16} height={16} />
              Export Enriched Report
            </button>
            <button
              type="button"
              className="bs-csv-enrichment__download-popover-item"
              onClick={() => {
                onFailedReport(job.id);
                setIsOpen(false);
              }}
            >
              <FileX width={16} height={16} />
              Export Failed Report
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};

const EnrichmentTable: React.FC<EnrichmentTableProps> = ({
  jobs,
  requestStatus,
  hasActiveFilters,
  page,
  pageSize,
  totalJobs,
  sort,
  onSort,
  onPaginationChange,
  onAddToProspects,
  onAddToSequence,
  onExport,
  onFailedReport,
  onDelete,
  onCancel,
  onNewEnrichment,
  onRetry,
  onViewJob,
}) => {
  const isLoading =
    requestStatus === RequestStatus.Pending ||
    requestStatus === RequestStatus.Ideal;

  const displayJobs = isLoading
    ? (Array.from({ length: SKELETON_ROWS }, (_, i) => ({
        id: `skeleton-${i}`,
      })) as EnrichmentJob[])
    : jobs;

  const columns = useMemo<ColumnDef<EnrichmentJob, any>[]>(
    () => [
      columnHelper.accessor('fileName', {
        id: 'fileName',
        header: 'File Name',
        size: 220,
        enableSorting: true,
        meta: { sortBy: 'fileName' },
        cell: ({ row }) => {
          const isFailed = row.original.status === EnrichmentStatus.FAILED;
          if (isFailed) {
            return (
              <span className="bs-csv-enrichment__file-name-static">
                <TruncatedTextWithAutoTooltip text={row.original.fileName} />
              </span>
            );
          }
          return (
            <button
              type="button"
              className="bs-csv-enrichment__file-name-btn"
              onClick={() => {
                onViewJob?.(row.original.id);
              }}
            >
              <TruncatedTextWithAutoTooltip text={row.original.fileName} />
            </button>
          );
        },
      }),
      columnHelper.accessor('createdAt', {
        id: 'createdAt',
        header: 'Date',
        size: 140,
        enableSorting: true,
        meta: { sortBy: 'createdAt' },
        cell: ({ getValue }) => (
          <span className="">
            {DateTime.fromISO(getValue()).toFormat('MMM dd, yyyy')}
          </span>
        ),
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: 'Status',
        size: 160,
        cell: ({ row }) => {
          const {
            status,
            progress,
            totalRecords,
            enrichedRecords,
            failedRecords,
          } = row.original;
          return (
            <span className={getStatusColorClass(status)}>
              <span className="semibold-2">
                {getStatusDisplayLabel(status)}
              </span>
              {status === EnrichmentStatus.PROCESSING && (
                <OverlayTooltip
                  className="bs-csv-enrichment__progress-tooltip"
                  text={
                    <div>
                      <div className="bs-csv-enrichment__progress-tooltip__header">
                        Progress Details
                      </div>
                      <div className="bs-csv-enrichment__progress-tooltip__row">
                        <span className="bs-csv-enrichment__progress-tooltip__label">
                          Total Records
                        </span>
                        <span className="bs-csv-enrichment__progress-tooltip__value">
                          {totalRecords?.toLocaleString()}
                        </span>
                      </div>
                      <div className="bs-csv-enrichment__progress-tooltip__row">
                        <span className="bs-csv-enrichment__progress-tooltip__label">
                          Enriched
                        </span>
                        <span className="bs-csv-enrichment__progress-tooltip__value bs-csv-enrichment__progress-tooltip__value--enriched">
                          {enrichedRecords?.toLocaleString()}
                        </span>
                      </div>
                      <div className="bs-csv-enrichment__progress-tooltip__row bs-csv-enrichment__progress-tooltip__row--last">
                        <span className="bs-csv-enrichment__progress-tooltip__label">
                          Not Enriched
                        </span>
                        <span className="bs-csv-enrichment__progress-tooltip__value bs-csv-enrichment__progress-tooltip__value--not-found">
                          {failedRecords?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  }
                  placement={Placement.Bottom}
                >
                  <div className="bs-csv-enrichment__progress-inline">
                    <ProgressBar
                      now={progress}
                      className="bs-csv-enrichment__progress-bar"
                    />
                    <span className="progress-count">{progress}%</span>
                  </div>
                </OverlayTooltip>
              )}
            </span>
          );
        },
      }),
      columnHelper.accessor('type', {
        id: 'type',
        header: 'Type',
        size: 120,
        cell: ({ getValue }) => (
          <span>
            {getValue() === EnrichmentType.PEOPLE ? 'People' : 'Company'}
          </span>
        ),
      }),
      columnHelper.accessor('revealType', {
        id: 'revealType',
        header: 'Components',
        size: 160,
        cell: ({ row }) => (
          <span>
            {row.original.type === EnrichmentType.COMPANY
              ? 'Full Profile'
              : getComponentDisplayLabel(row.original.revealType)}
          </span>
        ),
      }),
      columnHelper.accessor('totalRecords', {
        id: 'totalRecords',
        header: 'Total Uploaded Records',
        size: 140,
        enableSorting: true,
        meta: { sortBy: 'totalRecords' },
        cell: ({ getValue }) => <span>{getValue()?.toLocaleString()}</span>,
      }),
      columnHelper.accessor('enrichedRecords', {
        id: 'enrichedRecords',
        header: 'Total Enriched Records',
        size: 160,
        enableSorting: true,
        meta: { sortBy: 'enrichedRecords' },
        cell: ({ row }) => (
          <div>
            <div>{row?.original?.enrichedRecords?.toLocaleString()}</div>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Action',
        size: 170,
        cell: ({ row }) => {
          const job = row.original;
          const disableProspectAndSequence =
            job.status === EnrichmentStatus.PROCESSING ||
            job.status === EnrichmentStatus.FAILED ||
            job.status === EnrichmentStatus.QUEUED ||
            job.enrichedRecords === 0;
          return (
            <div className="bs-csv-enrichment__actions">
              {job.type === EnrichmentType.PEOPLE && (
                <>
                  <OverlayTooltip
                    text={
                      job.areProspectsCreated
                        ? 'Prospect Saved'
                        : 'Save as Prospect'
                    }
                    placement={Placement.Bottom}
                  >
                    <button
                      type="button"
                      className="bs-csv-enrichment__action-btn"
                      disabled={
                        disableProspectAndSequence || job.areProspectsCreated
                      }
                      onClick={() =>
                        !disableProspectAndSequence &&
                        !job.areProspectsCreated &&
                        onAddToProspects(job.id)
                      }
                    >
                      {job.areProspectsCreated ? (
                        <UserCheck width={16} height={16} />
                      ) : (
                        <UserPlus width={16} height={16} />
                      )}
                    </button>
                  </OverlayTooltip>
                  <OverlayTooltip
                    text="Add to Sequence"
                    placement={Placement.Bottom}
                  >
                    <button
                      type="button"
                      className="bs-csv-enrichment__action-btn"
                      disabled={disableProspectAndSequence}
                      onClick={() =>
                        !disableProspectAndSequence && onAddToSequence(job.id)
                      }
                    >
                      <Send width={16} height={16} />
                    </button>
                  </OverlayTooltip>
                </>
              )}
              <DownloadAction
                job={job}
                onExport={onExport}
                onFailedReport={onFailedReport}
              />
              {job.status === EnrichmentStatus.PROCESSING ||
              job.status === EnrichmentStatus.QUEUED ? (
                <OverlayTooltip
                  text="Cancel Enrichment"
                  placement={Placement.Bottom}
                >
                  <button
                    type="button"
                    className="bs-csv-enrichment__action-btn"
                    onClick={() => onCancel(job.id)}
                  >
                    <Cross width={16} height={16} />
                  </button>
                </OverlayTooltip>
              ) : (
                <OverlayTooltip text="Delete" placement={Placement.Bottom}>
                  <button
                    type="button"
                    className="bs-csv-enrichment__action-btn"
                    onClick={() => onDelete(job.id)}
                  >
                    <Trash width={16} height={16} />
                  </button>
                </OverlayTooltip>
              )}
            </div>
          );
        },
      }),
    ],
    [
      onViewJob,
      onAddToProspects,
      onAddToSequence,
      onExport,
      onFailedReport,
      onCancel,
    ],
  );

  if (requestStatus === RequestStatus.Failed) {
    return (
      <div className="bs-csv-enrichment__error-state">
        <h3 className="semibold-4 gray-txt-9">Failed to load jobs</h3>
        <p className="regular-2 gray-txt-11">
          Something went wrong. Please try again.
        </p>
        <Button onClick={onRetry}>Retry</Button>
      </div>
    );
  }

  if (!isLoading && jobs?.length === 0) {
    if (hasActiveFilters) {
      return (
        <div className="bs-csv-enrichment__empty-state-filter">
          <EmptyList
            title="No results found"
            description="Try adjusting your search or filters."
            imgSrc={Images.NoSearchResultIcon}
            isVertical
          />
        </div>
      );
    }
    return (
      <div className="bs-csv-enrichment__empty-state">
        {/* <h3 className="semibold-4 gray-txt-9">No enrichment jobs yet</h3>
        <p className="regular-2 gray-txt-11">
          Upload a CSV file to start enriching your data
        </p> */}
        <div className="d-flex flex-column justify-content-center align-items-center">
          <img src={Images.EnrichFile} alt="empty-data" />
          <div className="d-flex flex-column text-center gap-8">
            <span className="title">CSV Enrichment</span>
            <span className="subtitle">
              Enrich any CSV file with up-to-date people or company data,
              including contact info.
            </span>
          </div>
        </div>

        <Button className="new-enrichment-btn" onClick={onNewEnrichment}>
          <Upload width={16} height={16} color="#fff" />
          Enrich my first file
        </Button>
      </div>
    );
  }

  return (
    <>
      <TanStackTable
        columns={columns}
        rows={displayJobs}
        isLoading={isLoading}
        syncLayoutOnContainerResize
        enableSorting={true}
        pinnedColumns={['fileName']}
        defaultPinnedColumns={['fileName']}
        immovableColumns={['fileName']}
        sort={
          sort
            ? {
                sortBy: API_TO_COLUMN_SORT_KEY[sort.sortBy] ?? sort.sortBy,
                order: sort.order,
              }
            : null
        }
        onSort={(columnId, order) => {
          const apiKey = COLUMN_TO_API_SORT_KEY[columnId];
          if (apiKey) onSort(apiKey, order);
        }}
        enableRowSelection={false}
        enableColumnResizing
        disableGoToPage
        paginationOptions={{
          page,
          limit: pageSize,
          totalRows: totalJobs,
          onPaginationOptionsChange: onPaginationChange,
        }}
        paginationSizeOptions={[25, 50, 100]}
        tableClassName="bs-csv-enrichment__list-table"
      />
    </>
  );
};

export default EnrichmentTable;
